import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@mailflare/ui";
import type {
  BulkAction,
  BulkMessageActionRequest,
  LabelId,
  ListMessagesQuery,
  MailboxId,
  MessageId,
  SystemFolder,
  ThreadId,
  UpdateMessageRequest,
} from "@mailflare/shared";

import { ApiError } from "@/lib/api-client";
import {
  patchMessagesInPages,
  removeMessagesFromPages,
  toggleLabelInPages,
  type MessagePages,
  type MessageSummaryPatch,
} from "@/lib/message-cache";
import { queryKeys } from "@/lib/query-keys";

import { bulkMessageAction, getMessage, getThread, listMessages, updateMessage } from "./api";

export interface MessagesQueryArgs {
  readonly mailboxId: MailboxId;
  readonly folder?: SystemFolder;
  readonly labelId?: LabelId;
  readonly isFlagged?: boolean;
  readonly limit?: number;
}

function buildListParams(args: MessagesQueryArgs): Partial<ListMessagesQuery> {
  return {
    limit: args.limit ?? 50,
    sortBy: "received_at",
    sortOrder: "desc",
    ...(args.folder !== undefined ? { folder: args.folder } : {}),
    ...(args.labelId !== undefined ? { labelId: args.labelId } : {}),
    ...(args.isFlagged !== undefined ? { isFlagged: args.isFlagged } : {}),
  };
}

export function useMessages(args: MessagesQueryArgs) {
  const params = buildListParams(args);
  return useInfiniteQuery({
    queryKey: queryKeys.messageList(args.mailboxId, params),
    queryFn: ({ pageParam }) =>
      listMessages(args.mailboxId, {
        ...params,
        ...(pageParam !== null && pageParam !== undefined ? { cursor: pageParam } : {}),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.pageInfo.hasMore ? lastPage.pageInfo.nextCursor : null,
  });
}

export function useMessage(messageId: MessageId | null) {
  return useQuery({
    queryKey: messageId !== null ? queryKeys.message(messageId) : (["message", "none"] as const),
    queryFn: () => getMessage(messageId as MessageId),
    enabled: messageId !== null,
  });
}

export function useThread(mailboxId: MailboxId, threadId: ThreadId | null) {
  return useQuery({
    queryKey:
      threadId !== null ? queryKeys.thread(mailboxId, threadId) : (["threads", "none"] as const),
    queryFn: () => getThread(mailboxId, threadId as ThreadId),
    enabled: threadId !== null,
  });
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

interface MutationContext {
  readonly previous: ReadonlyArray<[readonly unknown[], MessagePages | undefined]>;
}

export function useUpdateMessage(mailboxId: MailboxId) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const listKey = queryKeys.messages(mailboxId);

  return useMutation({
    mutationFn: (variables: {
      readonly messageId: MessageId;
      readonly body: UpdateMessageRequest;
    }) => updateMessage(variables.messageId, variables.body),
    onMutate: async (variables): Promise<MutationContext> => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueriesData<MessagePages>({ queryKey: listKey });
      const patch: MessageSummaryPatch = {
        ...(variables.body.isRead !== undefined ? { isRead: variables.body.isRead } : {}),
        ...(variables.body.isFlagged !== undefined ? { isFlagged: variables.body.isFlagged } : {}),
        ...(variables.body.folder !== undefined ? { folder: variables.body.folder } : {}),
      };
      queryClient.setQueriesData<MessagePages>({ queryKey: listKey }, (data) =>
        patchMessagesInPages(data, [variables.messageId], patch),
      );
      return { previous };
    },
    onError: (error, _variables, context) => {
      if (context !== undefined) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
      toast({ title: "Update failed", description: errorMessage(error), variant: "destructive" });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
}

function patchForAction(action: BulkAction): MessageSummaryPatch | null {
  switch (action) {
    case "mark_read":
      return { isRead: true };
    case "mark_unread":
      return { isRead: false };
    case "flag":
      return { isFlagged: true };
    case "unflag":
      return { isFlagged: false };
    case "archive":
      return { folder: "archive" };
    case "unarchive":
      return { folder: "inbox" };
    case "move_trash":
      return { folder: "trash" };
    case "untrash":
      return { folder: "inbox" };
    case "move_spam":
      return { folder: "spam" };
    case "unspam":
      return { folder: "inbox" };
    default:
      return null;
  }
}

export function useBulkMessageAction(mailboxId: MailboxId) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const listKey = queryKeys.messages(mailboxId);

  return useMutation({
    mutationFn: (body: BulkMessageActionRequest) => bulkMessageAction(body),
    onMutate: async (body): Promise<MutationContext> => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueriesData<MessagePages>({ queryKey: listKey });

      if (body.action === "delete_permanent") {
        queryClient.setQueriesData<MessagePages>({ queryKey: listKey }, (data) =>
          removeMessagesFromPages(data, body.ids),
        );
      } else if (body.action === "add_label" && body.labelId !== undefined) {
        queryClient.setQueriesData<MessagePages>({ queryKey: listKey }, (data) =>
          toggleLabelInPages(data, body.ids, body.labelId as string, true),
        );
      } else if (body.action === "remove_label" && body.labelId !== undefined) {
        queryClient.setQueriesData<MessagePages>({ queryKey: listKey }, (data) =>
          toggleLabelInPages(data, body.ids, body.labelId as string, false),
        );
      } else {
        const patch = patchForAction(body.action);
        if (patch !== null) {
          queryClient.setQueriesData<MessagePages>({ queryKey: listKey }, (data) =>
            patchMessagesInPages(data, body.ids, patch),
          );
        }
      }
      return { previous };
    },
    onError: (error, _body, context) => {
      if (context !== undefined) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
      toast({ title: "Action failed", description: errorMessage(error), variant: "destructive" });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
}
